<template>
    <div
        class="bubble bubble--fluid"
        :class="{
            'is-open': isOpen,
            'is-shrunk': isShrunk,
        }"
        :style="{ width, transform, opacity }"
    >

        <div class="bubbleName">{{ $t(name) }}</div>

        <div
            class="bubbleBG--fluid"
            :style="{
                transform: innerTransform
            }">
            <div
                class="fluid-bubble__filler"
                :class="'color-filter--' + backgroundColor.toLowerCase()"
                :style="`backgroundPosition: ${(isFillReady) ? 708 : 0}px 0; transform: translateY(${95 - fill}%);`">
            </div>
        </div>
    </div>


</template>



<script>
import gsap from "gsap";
import Packer from "./Packer/Packer";

import { random } from "../../utils/utils";

/* BUBBLE
    ---------------------------------------------------------------------------------------------------- */

export default {

    /* Settings
        --------------------------------------------------------- */

    name: "Bubble",


    components: {
    },


    props: {
        value: {
            type: Number,
            required: true
        },
        name: { type: String, required: true },
        fill: { type: Number, required: true },

        maxDiameter: { type: Number, required: true },
        baseRadius: { type: Number, required: true },
        radius: { type: Number, required: true },
        backgroundColor: { type: String, required: true },

        isOpen: { type: Boolean, required: true },
        isShrunk: { type: Boolean, required: true },

        position: { type: Object, required: true },
        hasMoved: { type: Boolean, required: true },

        isFillReady: {
            type: Boolean,
            required: true
        }
    },


    data() {
        return {
            isMobile: false,
            spacing: 25,
            packedBubbles: [],
            bubbles: [],
        };
    },


    computed: {
        diameter() {
            return this.$props.radius * 2;
        },


        width() {
            return `${this.maxDiameter}px`;
        },


        opacity() {
            return this.$props.hasMoved ? 1 : 0;
        },


        transform() {
            const { x, y } = this.$props.position;
            const tX = Math.round((x - (this.maxDiameter / 2)) * 10) / 10;
            const tY = Math.round((y - (this.maxDiameter / 2)) * 10) / 10;

            return `translate3d(${tX}px, ${tY}px, 0)`;
        },


        innerTransform() {
            const scale = this.diameter / this.$props.maxDiameter;

            return `translate3d(0,0,0) scale(${scale})`;
        },

        Packer() {
            return new Packer({
                spacing: this.spacing,
                onMove: this.onMove,
            });
        },

        /* Dots (sub bubbles)
            --------------------------- */

        subWidth() {
            const squareSide = Math.sqrt(Math.pow(this.maxDiameter, 2) / 2);

            return squareSide * 1.1;
        },


        subWidthString() {
            return `${this.subWidth}px`;
        },


        subOffset() {
            return `calc(50% - ${this.subWidth / 2}px)`;
        },
    },




    /* Data handling
        --------------------------------------------------------- */

    watch: {
        bubbles(bubbles) {
            const newBubbles = bubbles.filter(b => !this.$data.packedBubbles.includes(b.id));

            this.Packer.addCircles(newBubbles);
            this.$data.packedBubbles.push(...newBubbles.map(b => b.id));
        },


        isOpen() {
            this.animateBubbles(0.8, true);
        },


        subWidth(subWidth) {
            this.resetPacker();
            this.setBubblesSize();
            this.animateBubbles(0);
        }
    },




    /* Life cycle
        --------------------------------------------------------- */

    created() {
        //
    },


    mounted() {
        // Responsive Behavior
        /* istanbul ignore next */
        this.$responsive.registerMediaQuery("from-medium", () => {
            this.isMobile = false;
            this.spacing = 0;
        }, () => {
            this.isMobile = true;
            this.spacing = 25;
        });


        this.reset();
        this.resetPacker();

        this.Packer.start();
    },


    destroyed() {
        this.$emit("removed", this.$props.name);
    },




    /* Methods
        --------------------------------------------------------- */

    methods: {

        /* Handlers
            --------------------------------------------------------- */

        onMove(pos) {
            this.$data.bubbles.forEach((b, i) => {
                const newPos = pos[b.name];
                if (!newPos) return;

                b.position.x = Math.round(newPos.position.x * 10) / 10;
                b.position.y = Math.round(newPos.position.y * 10) / 10;
                b.radius = newPos.radius;
            });
        },




        /* Actions
            --------------------------------------------------------- */

        resetPacker() {
            this.Packer.setBounds({
                width: this.subWidth,
                height: this.subWidth,
            });

            this.Packer.setTarget({
                x: this.subWidth / 2,
                y: this.subWidth / 2,
            });
        },


        setBubblesSize() {
            this.$data.bubbles.forEach((b) => {
                b.baseRadius = this.getRadius(b.type);
            });
        },


        animateBubbles(dur = 0.8, randomPos) {
            const { isOpen } = this.$props;
            const { bubbles } = this.$data;
            const Packer = this.Packer;

            const values = bubbles.map(b => ({
                name: b.name,
                r: b.radius,
                baseRadius: b.baseRadius,
            }));

            values.forEach((v, i) => {
                const x = randomPos ? this.getRandomPos(v.r) : bubbles[i].position.x;
                const y = randomPos ? this.getRandomPos(v.r) : bubbles[i].position.y;

                if (isOpen) {
                    Packer.setCirclePosition(v.name, x, y);
                }

                gsap.to(v, dur, {
                    r: isOpen ? v.baseRadius : 0,
                    ease: "power3.inOut",
                    onUpdate: () => {
                        Packer.setCircleRadius(v.name, v.r);
                    },
                    onComplete: () => {
                        if (!isOpen) {
                            Packer.setCirclePosition(v.name, x, y);
                        }
                    },
                });
            });
        },




        /* Values
            --------------------------------------------------------- */

        reset() {
            // this.$data.bubbles = this.formatData(this.$props.data);
        },


        getRadius(type) {
            const w = this.subWidth;
            return (type === "QQ" || type === "DD") ? (w / 5) : (w / 6);
        },


        getRandomPos(radius) {
            const c = this.subWidth / 2;

            return c - radius + (random(-1, 1) * random(0, c));
        },


        // formatData(data) {
        //     return data.map((b) => {
        //         const radius = this.getRadius(b.type);

        //         let x = this.getRandomPos(radius);
        //         let y = this.getRandomPos(radius);

        //         return {
        //             id: b.name,
        //             name: b.name,
        //             type: b.type,
        //             radius: 0.001,
        //             baseRadius: radius,
        //             position: { x, y },
        //         };
        //     });
        // }

    }

};
</script>
